<template>
  <div class="flex flex-wrap items-center gap-2" data-testid="document-queue-filter-bar">
    <label class="relative flex items-center">
      <span class="sr-only">Buscar médico ou CRM</span>
      <!-- O backend recusa busca acima de 100 caracteres (400): o campo já não deixa passar. -->
      <input
        type="search"
        :value="searchText"
        maxlength="100"
        placeholder="Buscar médico ou CRM"
        class="h-8 w-64 rounded-md border border-lightGray30 px-3 text-sm"
        data-testid="document-queue-search-input"
        @input="onSearchInput(($event.target as HTMLInputElement).value)"
      />
    </label>

    <SelectFilter
      v-for="select in selects"
      :key="select.key"
      :label="select.label"
      :options="select.options"
      :searchable="select.searchable"
      :is-open="openFilter === select.key"
      :selected-value="filters[select.key]"
      dropdown-width="w-64"
      :data-testid="`document-queue-${select.testId}-filter`"
      @toggle="toggleOpen(select.key)"
      @select="(value: string) => onSelect(select.key, value)"
      @click.stop
    />

    <button
      v-if="hasFilters"
      type="button"
      class="text-sm font-semibold text-primary30 underline"
      data-testid="document-queue-clear-filters"
      @click="clear"
    >
      Limpar filtros
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import SelectFilter from '@/components/Filters/SelectFilter.vue'
import { DOCUMENT_STATUS_LABEL } from '@/components/ProfessionalDocuments/labels'
import { EMPTY_REVIEW_FILTERS } from '@/pinia-store/useDocumentReviewStore'
import type { ReviewQueueFilters } from '@/pinia-store/useDocumentReviewStore'
import { useProfessionalDocumentCatalogStore } from '@/pinia-store/useProfessionalDocumentCatalogStore'
import { ProfessionalDocumentStatus } from '@/types/professional-documents.types'

import { REVIEW_TAG_OPTIONS } from '../constants'

type SelectKey = 'tag' | 'status' | 'typeKey'

const SEARCH_DEBOUNCE_MS = 300

const props = defineProps<{ filters: ReviewQueueFilters }>()
const emit = defineEmits<{ change: [filters: ReviewQueueFilters] }>()

const catalog = useProfessionalDocumentCatalogStore()

const TAG_OPTIONS = REVIEW_TAG_OPTIONS.map(({ title, value }) => ({ label: title, value }))
const STATUS_OPTIONS = Object.values(ProfessionalDocumentStatus).map((value) => ({
  label: DOCUMENT_STATUS_LABEL[value],
  value
}))

const selects = computed(() => [
  { key: 'tag' as const, testId: 'tag', label: 'Tags', options: TAG_OPTIONS, searchable: false },
  {
    key: 'status' as const,
    testId: 'status',
    label: 'Status do documento',
    options: STATUS_OPTIONS,
    searchable: false
  },
  {
    key: 'typeKey' as const,
    testId: 'type',
    label: 'Tipo de documento',
    options: catalog.types.map((type) => ({ label: type.name, value: type.key })),
    searchable: true
  }
])

/* ---------------------------------------------------------------- busca */

const searchText = ref(props.filters.search)
let searchDebounce: ReturnType<typeof setTimeout> | undefined
const cancelSearchDebounce = () => clearTimeout(searchDebounce)

const hasFilters = computed(() =>
  Boolean(
    searchText.value.trim() || props.filters.tag || props.filters.status || props.filters.typeKey
  )
)

function onSearchInput(value: string) {
  searchText.value = value
  cancelSearchDebounce()
  searchDebounce = setTimeout(
    () => emit('change', { ...props.filters, search: value }),
    SEARCH_DEBOUNCE_MS
  )
}

// Mudança de fora (ex.: limpar pela tela): o campo acompanha e a busca pendente cai.
watch(
  () => props.filters.search,
  (search) => {
    if (search === searchText.value) return
    cancelSearchDebounce()
    searchText.value = search
  }
)

/* ---------------------------------------------------------------- selects */

const openFilter = ref<SelectKey | null>(null)
const toggleOpen = (key: SelectKey) => {
  openFilter.value = openFilter.value === key ? null : key
}

/** Selecionar a opção já ativa desmarca o filtro. */
function onSelect(key: SelectKey, value: string) {
  cancelSearchDebounce()
  openFilter.value = null
  const next = props.filters[key] === value ? null : value
  // O valor vem das opções desta chave (tag/status/tipo do catálogo).
  emit('change', { ...props.filters, search: searchText.value, [key]: next } as ReviewQueueFilters)
}

function clear() {
  cancelSearchDebounce()
  searchText.value = ''
  openFilter.value = null
  emit('change', { ...EMPTY_REVIEW_FILTERS })
}

const closeOnOutsideClick = () => {
  openFilter.value = null
}
onMounted(() => document.addEventListener('click', closeOnOutsideClick))
onBeforeUnmount(() => {
  document.removeEventListener('click', closeOnOutsideClick)
  cancelSearchDebounce()
})
</script>
